// @ts-check
import { gql } from '@apollo/client';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import React from 'react';
import {
  Box,
  TabbedMenu,
  TabbedMenuItem,
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from '~/design-system';
import { importTypes } from '../types/importTypes';

export default function ChangesPreviewTable({
  rows,
  importData,
  columns,
  ...rest
}) {
  const { activeTab, setActiveTab } = useChangesTable();

  const rowsWithAdd = rows.filter(({ changeCode }) =>
    ['ADD'].includes(changeCode)
  );
  const rowsWithUpdate = rows.filter(({ changeCode }) =>
    ['UPDATE', 'MOVE'].includes(changeCode)
  );
  const rowsWithoutChanges = rows.filter(({ changeCode }) =>
    ['NO_CHANGE'].includes(changeCode)
  );

  return (
    <Box {...rest}>
      <ChangesTabbedMenu
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        allRowsCount={rows.length}
        rowsWithAddCount={rowsWithAdd.length}
        rowsWithUpdateCount={rowsWithUpdate.length}
        rowsWithoutChangesCount={rowsWithoutChanges.length}
      />
      <ChangesTable
        activeTab={activeTab}
        columns={columns}
        allRows={rows}
        rowsWithAdd={rowsWithAdd}
        rowsWithUpdate={rowsWithUpdate}
        rowsWithoutChanges={rowsWithoutChanges}
        importData={importData}
      />
    </Box>
  );
}

function ChangesTable({
  activeTab,
  columns,
  allRows,
  rowsWithAdd,
  rowsWithUpdate,
  rowsWithoutChanges,
  importData,
}) {
  if (activeTab === 'all')
    return (
      <Table sx={{ width: '100%' }}>
        <DefaultTableHeader columns={columns} />
        {allRows.map((row, index) => (
          <DefaultTableRow
            key={index}
            row={row}
            data={importData.rows.nodes.find(
              (dataRow) => dataRow.rowKey === row.rowKey
            )}
            columns={columns}
            type={importData.type}
          />
        ))}
      </Table>
    );
  if (activeTab === 'ADD' && rowsWithAdd.length === 0)
    return (
      <Box sx={{ paddingY: 3 }}>
        <Text>No rows that will add new entries.</Text>
      </Box>
    );

  if (activeTab === 'ADD' && rowsWithAdd.length > 0)
    return (
      <Table sx={{ width: '100%' }}>
        <DefaultTableHeader columns={columns} />
        {rowsWithAdd.map((row, index) => (
          <DefaultTableRow
            key={index}
            row={row}
            data={importData.rows.nodes.find(
              (dataRow) => dataRow.rowKey === row.rowKey
            )}
            columns={columns}
            type={importData.type}
          />
        ))}
      </Table>
    );

  if (activeTab === 'UPDATE' && rowsWithUpdate.length === 0)
    return (
      <Box sx={{ paddingY: 3 }}>
        <Text>No rows that will update data.</Text>
      </Box>
    );

  if (activeTab === 'UPDATE' && rowsWithUpdate.length > 0)
    return (
      <Table sx={{ width: '100%' }}>
        <DefaultTableHeader columns={columns} />
        {rowsWithUpdate.map((row, index) => (
          <DefaultTableRow
            key={index}
            row={row}
            data={importData.rows.nodes.find(
              (dataRow) => dataRow.rowKey === row.rowKey
            )}
            columns={columns}
            type={importData.type}
          />
        ))}
      </Table>
    );

  if (activeTab === 'NO_CHANGE' && rowsWithoutChanges.length === 0)
    return (
      <Box sx={{ paddingY: 3 }}>
        <Text>All rows have changes.</Text>
      </Box>
    );
  if (activeTab === 'NO_CHANGE' && rowsWithoutChanges.length > 0)
    return (
      <Table sx={{ width: '100%' }}>
        <DefaultTableHeader columns={columns} />
        {rowsWithoutChanges.map((row, index) => (
          <DefaultTableRow
            key={index}
            row={row}
            data={importData.rows.nodes.find(
              (dataRow) => dataRow.rowKey === row.rowKey
            )}
            columns={columns}
            type={importData.type}
          />
        ))}
      </Table>
    );
  return null;
}

function ChangesTabbedMenu({
  activeTab,
  setActiveTab,
  allRowsCount,
  rowsWithAddCount,
  rowsWithUpdateCount,
  rowsWithoutChangesCount,
  ...rest
}) {
  return (
    <TabbedMenu sx={{ margin: 0 }} {...rest}>
      <TabbedMenuItem
        isActive={activeTab === 'ADD'}
        onClick={() => setActiveTab('ADD')}
      >
        <Trans>Add ({rowsWithAddCount})</Trans>
      </TabbedMenuItem>
      <TabbedMenuItem
        isActive={activeTab === 'UPDATE'}
        onClick={() => setActiveTab('UPDATE')}
      >
        <Trans>Update ({rowsWithUpdateCount})</Trans>
      </TabbedMenuItem>
      <TabbedMenuItem
        isActive={activeTab === 'NO_CHANGE'}
        onClick={() => setActiveTab('NO_CHANGE')}
      >
        <Trans>No change ({rowsWithoutChangesCount})</Trans>
      </TabbedMenuItem>
      <TabbedMenuItem
        isActive={activeTab === 'all'}
        onClick={() => setActiveTab('all')}
      >
        <Trans>All ({allRowsCount})</Trans>
      </TabbedMenuItem>
    </TabbedMenu>
  );
}

function DefaultTableHeader({ columns }) {
  const { i18n } = useLingui();
  return (
    <TableHead>
      <TableRow>
        <TableHeader sx={{ backgroundColor: 'brand.lightGray', width: 100 }}>
          <Text variant="smallest" sx={{ textAlign: 'center' }}>
            <Trans>Row</Trans>
          </Text>
        </TableHeader>
        <TableHeader sx={{ backgroundColor: 'brand.lightGray', width: 100 }}>
          <Text variant="smallest" sx={{ textAlign: 'left' }}>
            <Trans>Action</Trans>
          </Text>
        </TableHeader>
        {columns.map(({ label }, index) => (
          <TableHeader
            key={index}
            sx={{ backgroundColor: 'brand.lightGray', width: 250 }}
          >
            <Text variant="smallest">{i18n._(label)}</Text>
          </TableHeader>
        ))}
      </TableRow>
    </TableHead>
  );
}

function DefaultTableRow({
  row: { rowKey, changeCode, changes },
  data,
  columns,
  type,
}) {
  const { mappedChanges, colors } = changes.reduce(
    (acc, change) => {
      const color =
        change.to && !change.from
          ? // Green is no conflicts
          'brand.successGreen'
          : // Yellow includes any changes
          'brand.warningYellow';
      // Red will be used for deletions
      acc.mappedChanges.push({
        ...change,
        color,
      });
      acc.colors.push(color);

      return acc;
    },
    { mappedChanges: [], colors: [] }
  );
  const rowColor = colors.includes('brand.warningYellow')
    ? 'brand.warningYellow'
    : colors.includes('brand.successGreen')
      ? 'brand.successGreen'
      : 'transparent';
  return (
    <TableRow>
      <TableCell
        sx={{
          height: 65,
          borderLeft: `2px solid`,
          borderLeftColor: rowColor,
        }}
      >
        <Text>{rowKey}</Text>
      </TableCell>
      <TableCell sx={{ height: 65 }}>
        <Text sx={{ textAlign: 'left' }}>{changeCode}</Text>
      </TableCell>
      {columns.map(({ bottlebooksFieldName }, index) => {
        const change = mappedChanges.find(
          (change) => change.bottlebooksFieldName === bottlebooksFieldName
        );
        return (
          <TableCell
            key={index}
            sx={{
              height: 65,
              borderLeft: `2px solid`,
              borderLeftColor: change?.color ? change?.color : 'transparent',
            }}
          >
            <ImportValue
              bottlebooksFieldName={bottlebooksFieldName}
              values={data.values}
              changes={changes}
              sx={{ textAlign: 'left' }}
              type={type}
            />
            <PreviousValue
              bottlebooksFieldName={bottlebooksFieldName}
              changes={changes}
              sx={{ textAlign: 'left' }}
            />
          </TableCell>
        );
      })}
    </TableRow>
  );
}

function useChangesTable() {
  const [activeTab, setActiveTab] = React.useState('ADD');
  return {
    activeTab,
    setActiveTab,
  };
}

function ImportValue({ bottlebooksFieldName, values, changes, type, ...rest }) {
  let importValue = values.find(
    (change) => change.bottlebooksFieldName === bottlebooksFieldName
  )?.rawText;
  if (type === importTypes.productMasterListImport) {
    if (bottlebooksFieldName.includes('.inventoryId')) {
      importValue = changes.find(
        (change) => change.bottlebooksFieldName === bottlebooksFieldName
      )?.to;
    }
  }
  if (importValue) return <Text {...rest}>{importValue}</Text>;
  if (!getPreviousValue({ bottlebooksFieldName, changes })) return null;
  return (
    <Text sx={{ color: 'brand.warningRed' }} {...rest}>
      Delete
    </Text>
  );
}

function getPreviousValue({ bottlebooksFieldName, changes }) {
  return changes.find(
    (change) => change.bottlebooksFieldName === bottlebooksFieldName
  )?.from;
}

function PreviousValue({ bottlebooksFieldName, changes, ...rest }) {
  const change = changes.find(
    (change) => change.bottlebooksFieldName === bottlebooksFieldName
  );
  const previousValue = change?.from;

  // Previous value was not null
  if (change && previousValue)
    return (
      <Text sx={{ color: 'brand.lightSmoke' }} {...rest}>
        Previous: {previousValue}
      </Text>
    );

  // Previous value was null
  if (change && !previousValue)
    return (
      <Text sx={{ color: 'brand.lightSmoke' }} {...rest}>
        No previous value
      </Text>
    );

  // No change was reported
  return null;
}

ChangesPreviewTable.fragment = gql`
  fragment ChangesPreviewTable on DataImport {
    bottlebooksFields {
      bottlebooksFieldName
    }
    rows {
      totalCount
      nodes {
        rowKey
        values {
          bottlebooksFieldName
          rawText
        }
      }
    }
    preview {
      object
      changes {
        rowKey
        changeCode
        changes {
          bottlebooksFieldName
          changeCode
          to
          from
        }
      }
    }
  }
`;
